<template>
  <form class="send-message-form"
        name="sendMessageForm"
        @submit.prevent="!$v.$invalid && submitForm()"
        novalidate
        autocomplete="off">
    <section class="extended">
      <!-- Patient field -->
      <user-input-list v-model="recipientName"
                       :name="'recipientName'"
                       id="user-list-search"
                       :default-suggestion="defaultRecipient"
                       :suggestions="patientsList"
                       :validation="$v.recipientName"
                       :label="$t('copy_354')"
                       :autocompleteOff="true"
                       :is-editable="hasEditableRecipient"
                       @selected-suggestion="handleSelectSuggestion"
                       @reset-suggestions="$emit('reset-suggestions')">
      </user-input-list>
      <hr />
      <!-- Subject field -->
      <sword-text-wuk name="subject"
                      v-model="subject"
                      ref="subject"
                      :validation="$v.subject"
                      :placeholder="$t('copy_355')"
                      autocomplete="off" />
      <hr />
      <!-- Message field -->
      <sword-textarea-wuk v-model="body"
                          name="body"
                          class="body-textarea"
                          :validation="$v.body"
                          :placeholder="$t('copy_356')"
                          autocomplete="off" />
      <hr />
    </section>
    <section class="form-buttons">
      <sword-button-wuk theme="secondary"
                        :aria-label="$t('COMMON.CANCEL')"
                        class="cancel"
                        text="COMMON.CANCEL"
                        icon="close"
                        side="left"
                        @click.native="cancel">
      </sword-button-wuk>
      <sword-button-wuk type="submit"
                        :disabled="$v.$invalid || !recipient"
                        :aria-label="$t('copy_357')"
                        text="copy_357"
                        icon="send">
      </sword-button-wuk>
    </section>
  </form>
</template>

<script>
import { parseValidationsStr } from '@sword-health/input-validation';

import SwordButtonWuk from '@ui-kit/components/buttons/SwordButton.vue';
import SwordTextWuk from '@ui-kit/components/inputs/SwordText.vue';
import SwordTextareaWuk from '@ui-kit/components/inputs/SwordTextarea.vue';
import UserInputList from '@ui-kit/components/dt-messages/UserInputList.vue';

const TYPING_TIMER = 300; // in milliseconds

export default {
  name: 'messageForm',
  components: {
    SwordButtonWuk,
    SwordTextWuk,
    SwordTextareaWuk,
    UserInputList,
  },
  props: {
    sending: {
      type: Boolean,
      default: false,
    },
    defaultRecipient: {
      type: Object,
    },
    patientsList: {
      type: Array,
      default: () => [],
    },
    hasEditableRecipient: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['submit-form', 'cancel'],
  data() {
    return {
      recipientName: '',
      recipient: null,
      subject: '',
      body: '',
    };
  },
  computed: {
    typingDebounce() {
      let timeout;
      return function () {
        const that = this;
        const later = function () {
          timeout = null;
          that.fetchPatients();
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, TYPING_TIMER);
      };
    },
  },
  validations: {
    recipientName: parseValidationsStr({
                                         required: true,
                                         pack: 'fullname',
                                       }),
    subject: parseValidationsStr({
                                   required: true,
                                   pack: 'fullname',
                                 }),
    body: parseValidationsStr({
                                required: true,
                                pack: 'freeField',
                              }),
  },
  methods: {
    handleSelectSuggestion(selectedRecipient) {
      this.recipient = selectedRecipient;
    },
    submitForm() {
      if (!this.recipient) {
        return;
      }

      const messageData = {
        patientRefID: this.recipient.user_id,
        subject: this.subject,
        text: this.body,
      };

      this.$emit('submit-form', messageData);
    },
    cancel() {
      this.$emit('cancel');
    },
    fetchPatients() {
      if (this.recipientName.length < 3) {
        return;
      }
      this.$emit('fetch-patients', this.recipientName);
    },
  },
  watch: {
    recipientName() {
      this.typingDebounce();
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .field-wrapper-wuk {
  margin-bottom: 0;
  position: relative;
  display: flex;

  .sword-help-text-wuk {
    margin: 0;
  }

  .sword-input-error-wuk {
    text-align: right;
    margin: 0;
  }

  &.sword-textarea-wuk {
    flex-grow: 1;
    flex-shrink: 1;

    textarea {
      overflow-y: auto;
    }
  }

  .sword-base-input-wuk {
    line-height: 1.5rem;
  }
}

.body-textarea {
  height: 100%;
  display: flex;
  flex-direction: column;

  ::v-deep .sh-maxlength-error {
    margin-top: -1.25rem !important;
  }

  ::v-deep .sh-input-error-message {
    bottom: -0.625rem;
  }
}

.send-message-form {
  @extend %flex-column-container;
  padding: 1rem;
  overflow: hidden;

  &.hide-form {
    z-index: -1;
  }

  > section.extended {
    @extend %flex-column;
  }

  .form-buttons {
    display: flex;
    justify-content: space-between;

    .sword-button-wuk {
      min-width: initial;
    }
  }
}

@media only screen and (max-device-height: $max-tablet-size) and (orientation: landscape) {
  .send-message-form {
    overflow: visible;
  }
}

@media only screen and (max-device-width: $max-tablet-size) and (orientation: portrait),
(max-device-height: $max-tablet-size) and (orientation: landscape) {
  .send-message-form {
    flex-grow: 1;
    height: 100%;
    overflow-y: auto;
    padding: 0;

    > section.extended {
      flex-shrink: 1;
      padding: 1.25rem;

      > .field-container:last-of-type {
        flex-grow: 1;
        display: flex;
        flex-direction: column;

        > textarea {
          flex-grow: 1;
          max-height: none;
          height: 100%;
        }
      }
    }
  }

  .search-dropdown-list {
    max-height: 10.625rem;
  }

  .sword-button-wuk {
    border-radius: 0;
    margin: 0;
    width: 100%;
  }

  .cancel {
    display: none;
  }
}
</style>
